
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { QuoteService } from '@/services/quote.service'
import { Quote } from '@/@types/responses/quote/Quote'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildTypeService } from '@/services/buildType.service'
import CustomerSearchMachine from '@/components/elements/CustomerSearchMachine.vue'
import { Customer } from '@/@types/responses/customer/Customer'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import Checkbox from '@/components/shared/Checkbox.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Quotes',
  components: {
    CartridgeSearchMachine,
    Checkbox,
    CustomerSearchMachine,
    Dropdown,
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      quoteService,
      toast
    }
  },
  data() {
    let buildTypeId: number|undefined
    let cartridgeId: number|undefined
    let customer: Customer|undefined

    return {
      ascending: true,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      buildTypeId,
      cartridgeId,
      customer,
      filterOptionsExpanded: true,
      isApproved: false,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      quotes: [] as Quote[],
      searchTerm: '',
      sortField: 'Id',
      totalResults: 0
    }
  },
  computed: {
    customerName(): string {
      if (!this.customer) return ''

      return `${this.customer.firstName} ${this.customer.lastName}`
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)

    this.fetchBuildTypeDropdownOptions().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void {
      this.buildTypeId = undefined
      this.cartridgeId = undefined
      this.customer = undefined
      this.isApproved = false
      this.searchTerm = ''
      this.search()
    },
    fetchBuildTypeDropdownOptions(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onCustomerSelected(customer: Customer): void {
      this.customer = customer
    },
    onDeleteClicked(id: number): void {
      this.quoteService.deleteQuote(id).then(() => {
        if ((this.quotes.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchQuotes()

        this.toast.success('Quote deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the quote. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchQuotes()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/quote/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const buildTypeIdFromUrl = this.getNumericParameterByName("bt")
      this.buildTypeId = buildTypeIdFromUrl !== undefined ? buildTypeIdFromUrl : this.buildTypeId

      const cartridgeIdFromUrl = this.getNumericParameterByName("ca")
      this.cartridgeId = cartridgeIdFromUrl !== undefined ? cartridgeIdFromUrl : this.cartridgeId

      const customerIdFromUrl = this.getNumericParameterByName("ci")
      const customerNameFromUrl = this.getStringParameterByName("cn")
      if (customerIdFromUrl !== undefined && customerNameFromUrl !== undefined)
        this.customer = new Customer(customerIdFromUrl, customerNameFromUrl.split(' ')[0], customerNameFromUrl.split(' ')[1], '', '', '', '', '', undefined, '')

      const isApprovedFromUrl = this.getBooleanParameterByName("i")
      this.isApproved = isApprovedFromUrl !== undefined ? isApprovedFromUrl : this.isApproved

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchQuotes()
    },
    search(): void {
      this.pageNumber = 1
      this.searchQuotes()
    },
    searchQuotes(): void {
      this.loading = true
      this.updateUrl()
      this.quoteService.searchQuotes(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.buildTypeId, this.cartridgeId, this.customer?.id, this.isApproved, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.quotes = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the quotes. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchQuotes()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}&i=${this.isApproved}`

      if (this.buildTypeId !== undefined)
        newQueryString = newQueryString + `&bt=${this.buildTypeId}`

      if (this.cartridgeId !== undefined)
        newQueryString = newQueryString + `&ca=${this.cartridgeId}`

      if (this.customer !== undefined) {
        newQueryString = newQueryString + `&ci=${this.customer.id}`
        newQueryString = newQueryString + `&cn=${encodeURIComponent(this.customerName)}`
      }

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(encodeURIComponent(this.searchTerm))}`
      
      this.$router.replace('/quotes' + newQueryString)
    }
  }
})
